import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Wallet, Plus, X, CheckCircle2, XCircle, Clock3 } from "lucide-react";
import { logger } from "@/lib/logger";
import { toast } from "sonner";

const fmtINR = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(Number(n) || 0);

const STATUS_META = {
  pending:   { bg: "bg-amber-50",   text: "text-amber-700",   label: "Pending",   icon: Clock3 },
  approved:  { bg: "bg-emerald-50", text: "text-emerald-700", label: "Approved",  icon: CheckCircle2 },
  disbursed: { bg: "bg-blue-50",    text: "text-blue-700",    label: "Disbursed", icon: CheckCircle2 },
  rejected:  { bg: "bg-red-50",     text: "text-red-700",     label: "Rejected",  icon: XCircle },
};

export default function MyAdvances() {
  const [data, setData] = useState({ items: [], advance_limit: 0, used: 0, available: 0 });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ amount: "", reason: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const res = await api.get("/advance-requests/me");
      setData(res.data);
    } catch (err) {
      logger.error("Failed to load my advances:", err);
      toast.error(err.response?.data?.detail || "Failed to load advance data");
    }
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    const n = Number(form.amount);
    if (!n || n <= 0) { toast.error("Enter an amount greater than zero"); return; }
    setBusy(true);
    try {
      await api.post("/advance-requests", { amount: n, reason: form.reason || null });
      toast.success("Request submitted for approval");
      setOpen(false);
      setForm({ amount: "", reason: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const cancel = async (r) => {
    if (!window.confirm("Cancel this pending request?")) return;
    try {
      await api.delete(`/advance-requests/${r.id}`);
      toast.success("Cancelled");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const noLimit = !data.advance_limit;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <span className="label-overline">Compensation</span>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-1">Advance Requests</h1>
          <p className="text-sm text-zinc-500 mt-2">Request a salary advance. Subject to your assigned limit and approval by admin.</p>
        </div>
        <Button onClick={() => setOpen(true)} disabled={noLimit} className="bg-zinc-950 hover:bg-zinc-800" data-testid="new-advance-button">
          <Plus className="w-4 h-4 mr-1" /> New request
        </Button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <Card label={data.advance_limit_type === "percent" ? `Your limit (${Number(data.advance_limit_value || 0)}% of salary)` : "Your limit"} value={fmtINR(data.advance_limit)} />
        <Card label="Used / Pending" value={fmtINR(data.used)} accent="amber" />
        <Card label="Available" value={fmtINR(data.available)} accent="green" />
      </div>

      {noLimit && (
        <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2" data-testid="no-limit-banner">
          {data.note || "You don't have an advance limit set yet. Please ask your manager to enable advances on your profile."}
        </div>
      )}

      <div className="bg-white border border-zinc-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr className="text-left text-zinc-500 uppercase text-[10px] tracking-widest">
              <th className="py-3 px-4">Date</th>
              <th className="py-3 px-4 text-right">Amount</th>
              <th className="py-3 px-4">Reason</th>
              <th className="py-3 px-4">Status</th>
              <th className="py-3 px-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((r) => {
              const m = STATUS_META[r.status] || STATUS_META.pending;
              const Icon = m.icon;
              return (
                <tr key={r.id} className="border-b border-zinc-100" data-testid={`my-advance-${r.id}`}>
                  <td className="py-2.5 px-4 text-zinc-600">{new Date(r.created_at).toLocaleDateString([], { day: "2-digit", month: "short", year: "numeric" })}</td>
                  <td className="py-2.5 px-4 text-right font-mono font-semibold">{fmtINR(r.amount)}</td>
                  <td className="py-2.5 px-4 text-zinc-600 max-w-xs truncate" title={r.reason || ""}>{r.reason || "—"}</td>
                  <td className="py-2.5 px-4">
                    <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded ${m.bg} ${m.text}`}>
                      <Icon className="w-3 h-3" /> {m.label}
                    </span>
                    {r.decision_note && <div className="text-[10px] text-zinc-500 italic mt-0.5">{r.decision_note}</div>}
                  </td>
                  <td className="py-2.5 px-4 text-right">
                    {r.status === "pending" && (
                      <button onClick={() => cancel(r)} className="p-1.5 hover:bg-red-50 hover:text-red-600 rounded" data-testid={`cancel-advance-${r.id}`}>
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
            {data.items.length === 0 && (
              <tr><td colSpan={5} className="py-10 text-center text-zinc-500"><Wallet className="w-6 h-6 mx-auto text-zinc-300" /><div className="mt-2">No requests yet.</div></td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="new-advance-dialog">
          <DialogHeader>
            <DialogTitle>Request a salary advance</DialogTitle>
            <DialogDescription>Your available limit: {fmtINR(data.available)}.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Amount (INR) <span className="text-red-500">*</span></Label>
              <Input type="number" min="1" step="100" value={form.amount}
                     onChange={(e) => setForm({ ...form, amount: e.target.value })}
                     placeholder="e.g. 2000" data-testid="advance-amount" />
            </div>
            <div>
              <Label>Reason (optional)</Label>
              <Textarea rows={3} value={form.reason}
                        onChange={(e) => setForm({ ...form, reason: e.target.value })}
                        placeholder="e.g. medical emergency, school fee" data-testid="advance-reason" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={submit} disabled={busy} className="bg-zinc-950 hover:bg-zinc-800" data-testid="advance-submit">Submit request</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Card({ label, value, accent }) {
  const cls = accent === "green" ? "bg-emerald-50 border-emerald-200 text-emerald-900" :
              accent === "amber" ? "bg-amber-50 border-amber-200 text-amber-900" :
              "bg-white border-zinc-200";
  return (
    <div className={`border rounded-md p-4 ${cls}`}>
      <div className="label-overline">{label}</div>
      <div className="text-2xl font-bold font-display mt-1 font-mono">{value}</div>
    </div>
  );
}
